import { useLicensingServiceV1 } from "@rw1/api-services";
import { CompanyData_V1, Licensing_V1 } from "@rw1/auto-generated-clients";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Experience,
  useAllExperiences,
} from "../../../hooks/useAllExperiences";
import { useCompaniesData } from "../../../hooks/useCompaniesData";
import { useFetchAll } from "../../../hooks/useFetchAll";
import { TCompanyLicenseRow } from "../types";

export const ALL_LICENSES_QUERY_KEY = "all-licenses";

type LicenseResponse = Licensing_V1.License & {
  ReleaseChannelId?: string;
  AssignedOrganizationKey?: string;
  contentMetadataId?: string;
};

// Transform a single license into a row
const mapLicenseToRow = (
  license: LicenseResponse,
  experiencesMap: Map<string, Experience>,
  companiesData: CompanyData_V1.Company[] | undefined,
): TCompanyLicenseRow => {
  const releaseChannelId =
    license.ReleaseChannelId ?? license.contentMetadataId ?? "";
  const assignedOrgKey =
    license.AssignedOrganizationKey ?? license.assignedOrganizationId;
  const isAssigned = Boolean(assignedOrgKey);

  // Look up related data
  const experience = experiencesMap.get(releaseChannelId);

  let companyName = "Unassigned";
  if (isAssigned) {
    const assignedCompany = companiesData?.find(
      (c) => c.companyId === assignedOrgKey,
    );
    companyName = assignedCompany?.friendlyName ?? "Unknown Company";
  }

  return {
    id: license.licenseId,
    companyId: assignedOrgKey || "",
    companyName,
    experienceId: releaseChannelId,
    experienceName: experience?.friendlyName ?? license.licenseName,
    seats: {
      used: license.allocatedSeats,
      total: license.seats ?? null,
    },
    expiresAt: license.expiresAt ?? null,
    assignedOrganizationId: license.assignedOrganizationId || "",
    releaseChannelId: license.releaseChannelId,
  };
};

export const useExternalLicenses = (
  enabled: boolean,
): {
  licenses: TCompanyLicenseRow[];
  loading: boolean;
} => {
  const licensingService = useLicensingServiceV1();
  const { companiesData, isLoading: isCompaniesLoading } = useCompaniesData();
  const { experiencesMap, isLoading: isExperiencesLoading } =
    useAllExperiences();

  const { companyId } = useParams();

  const { data: rawLicenses = [], isLoading: isLicensesLoading } =
    useFetchAll<Licensing_V1.License>(
      [ALL_LICENSES_QUERY_KEY, companyId],
      (page, size) =>
        licensingService.getAllByOrganizationId(companyId!, page, size),
      { enabled: !!enabled && !!companyId },
    );

  const licenses = useMemo(() => {
    if (!companyId || !rawLicenses.length) return [];

    const now = new Date();

    return rawLicenses
      .filter((license) => {
        // Filter by issuing organization
        if (license.issuingOrganizationId !== companyId) return false;

        // Filter out expired licenses
        if (license.expiresAt && new Date(license.expiresAt) < now)
          return false;

        return true;
      })
      .map((license) =>
        mapLicenseToRow(
          license as LicenseResponse,
          experiencesMap,
          companiesData,
        ),
      );
  }, [companyId, rawLicenses, experiencesMap, companiesData]);

  const loading =
    isLicensesLoading || isExperiencesLoading || isCompaniesLoading;

  return { licenses, loading };
};
